/**
 * <voucher-card> — one code, as the printed thing it is.
 *
 * WHY A CODE GETS THE LOUDEST LINE ON ITS OWN CARD. Nothing else in this
 * product leaves the screen. A code is printed on a flyer, read out on a
 * podcast, pasted into a newsletter and typed back in by a stranger — so the
 * operator's list is the one place they check the string they are about to
 * publish, and the one place they find it again when a partner phones to say
 * it does not work. It is set at `title/lg` against the `body/md` line beneath
 * (22 over 14 — the ~1.6 the type scale already offers), tabular so two codes
 * align digit under digit, and letter-spaced the way a printed reference is.
 *
 * THE FOUR LINES, IN THE ORDER THE OPERATOR ASKS FOR THEM: WHICH code · WHAT
 * it does · WHAT IT IS FOR · UNTIL WHEN. The effect line names the ticket
 * types a code reveals rather than counting them, because "which one" is the
 * question and "how many" is a different one.
 *
 * THE METER IS A RING, AND THE RING IS THE CARD'S SECOND READING. A code's
 * allowance is a quota — it burns down and can never be refilled — so it is
 * the same arc <sell-through-ring> already draws beside a ticket type, handed
 * its own sentence and its own stopped tone. The arc carries the share (4 of
 * 20 and 1.284 of 5.000 are unmistakably different shapes) and the figures
 * carry the size, which is what lets a two-use press code and a
 * five-thousand-use radio code be the same object without wanting different
 * cards.
 *
 * COPYING THE CODE. A press on the control must not also open the sheet, which
 * the card cannot work out for itself: it hangs its click on a shadow div every
 * slotted child's click bubbles through, and its Enter/Space handler sits on the
 * host filtered by key alone, never by where the press came from. So the control
 * stops the click, the activating key press, and the pointer press besides —
 * which would otherwise light the whole card's state layer for the press of
 * something that is not the card.
 *
 * WHAT A STOPPED CODE LOOKS LIKE. Its arc goes to the outline tone and its
 * effect line goes to on-surface-variant — the code no longer does that thing,
 * so the line saying it does steps back. The code string itself never dims:
 * the operator hunting a code is most often hunting the one that stopped, and
 * a list that hides its dead entries hides exactly the entry being looked for.
 * No colour is spent on either state — an exhausted press code did its whole
 * job and an exhausted newsletter code is turning buyers away, and nothing on
 * the document says which, so a green or a red would assert a valence the data
 * does not carry.
 */

import { formatCurrency, formatDate } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { tooltip } from "@mhmo91/schmancy/primitives";
import { html, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { consoleEventId } from "../events/console/console.state.ts";
import "../events/console/sell-through-ring.view.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import type { VoucherCardRow } from "./vouchers.state.ts";

@schmancyElement("voucher-card")
export class VoucherCard extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) card!: VoucherCardRow;
	@state() private copying = false;
	@state() private copyResult: "copied" | "failed" | null = null;

	override render(): TemplateResult {
		const card = this.card;
		const stopped = card.stop !== null;
		const copyAria = this.t(
			this.copyResult === "copied"
				? "admin.vouchers.list.copied"
				: this.copyResult === "failed"
					? "admin.vouchers.list.copyFailed"
					: "admin.vouchers.list.copyAria",
			{
				code: card.code,
			},
		);
		const percent = card.effect.discount?.type === "percent";
		const amount =
			card.effect.discount === null
				? ""
				: percent
					? String(card.effect.discount.value)
					: formatCurrency(
							card.effect.discount.value / 100,
							"EUR",
							this.locale,
						);
		// Where the discount is confined to exactly the types the code reveals —
		// the partner idiom — the money clause says "off it" rather than naming
		// the same ticket twice in one line.
		const money =
			card.effect.discount === null
				? ""
				: card.effect.scopeIsUnlocks
					? this.t(
							percent
								? "admin.vouchers.list.percentOnUnlocked"
								: "admin.vouchers.list.fixedOnUnlocked",
							{ value: amount },
						)
					: card.effect.scopeCount === 0
						? this.t(
								percent
									? "admin.vouchers.list.percentAll"
									: "admin.vouchers.list.fixedAll",
								{ value: amount },
							)
						: card.effect.scopeNames.length === 0
							? this.t(
									percent
										? "admin.vouchers.list.percentOnly"
										: "admin.vouchers.list.fixedOnly",
									{ value: amount },
								)
							: this.t(
									percent
										? "admin.vouchers.list.percentOn"
										: "admin.vouchers.list.fixedOn",
									{ value: amount, types: card.effect.scopeNames.join(", ") },
								);
		// A code revealing a type that has since been deleted has no name left to
		// print, and printing the id would be printing an id — the count is the
		// one place it survives.
		const reveal =
			card.effect.unlockCount === 0
				? ""
				: card.effect.unlockNames.length === card.effect.unlockCount
					? this.t("admin.vouchers.list.unlocks", {
							types: card.effect.unlockNames.join(", "),
						})
					: card.effect.unlockCount === 1
						? this.t("admin.vouchers.list.unlocksUnnamedOne")
						: this.t("admin.vouchers.list.unlocksUnnamedOther", {
								count: card.effect.unlockCount,
							});
		// A code carrying both halves says them in the order the buyer meets
		// them: the tier opens, and then the money comes off it.
		const effect = [reveal, money]
			.filter((clause) => clause !== "")
			.join(" · ");
		const expiresOn =
			formatDate(new Date(card.expiresAtMs ?? 0).toISOString(), this.locale, {
				day: "numeric",
				month: "short",
				year: "numeric",
			}) ?? "";
		// Expiry and the two terminal words, and nothing else: the per-person cap
		// sits on nearly every code, answers none of the questions this screen is
		// asked, and pushed this line onto a second row on a third of the cards.
		// It is one press away, on the code's own sheet.
		const deadline =
			card.stop === "expired"
				? this.t("admin.vouchers.list.expiredOn", { when: expiresOn })
				: card.stop === "spent"
					? this.t("admin.vouchers.list.usedUp")
					: card.expiresAtMs !== null
						? this.t("admin.vouchers.list.validUntil", { when: expiresOn })
						: "";

		return html`
			<schmancy-card
				type="outlined"
				interactive
				class="w-full rounded-2xl"
				aria-label="${card.code} — ${card.label}"
				@schmancy-click=${this.open}
			>
				<div class="flex items-start gap-4 p-4">
					<div class="flex min-w-0 flex-1 flex-col gap-1">
						<div class="flex min-w-0 items-center gap-1">
							<schmancy-typography
								type="title"
								token="lg"
								class="min-w-0 tabular-nums tracking-wide"
							>
								${card.code}
							</schmancy-typography>
							<schmancy-icon-button
								icon=${this.copyResult === "copied" ? "check" : this.copyResult === "failed" ? "error" : "content_copy"}
								.busy=${this.copying}
								color=${this.copyResult === "failed" ? "error" : "primary"}
								size="sm"
								aria-label=${copyAria}
								${tooltip(copyAria)}
								@click=${this.copy}
								@mousedown=${this.keepPressOffTheCard}
								@keydown=${this.keepPressOffTheCard}
							></schmancy-icon-button>
						</div>
						<schmancy-typography
							type="body"
							token="md"
							class=${this.classMap({ "text-surface-onVariant": stopped })}
						>
							${effect}
						</schmancy-typography>
						<schmancy-typography type="body" token="sm" maxLines="1" class="text-surface-onVariant">
							${card.label}
						</schmancy-typography>
						<!-- The terminal word keeps the surface's own ink while a
						     deadline still ahead steps back to on-surface-variant: on a
						     card whose other lines have just dimmed, the one line that
						     says WHY is the one line that must not. Status as typography
						     and placement is the whole treatment — no badge, and no
						     colour, since nothing on the document says whether an
						     exhausted code did its job or is turning buyers away. -->
						${when(
							deadline !== "",
							() => html`
								<schmancy-typography
									type="label"
									token="sm"
									class=${this.classMap({ "text-surface-onVariant": !stopped })}
								>
									${deadline}
								</schmancy-typography>
							`,
						)}
					</div>

					<sell-through-ring
						.sold=${card.usedCount}
						.capacity=${card.usageLimit}
						?stopped=${stopped}
						.accessibleName=${this.t("admin.vouchers.list.usedOf", {
							used: card.usedCount,
							limit: card.usageLimit,
						})}
					></sell-through-ring>
				</div>
			</schmancy-card>
		`;
	}

	private readonly copy = async (event: Event): Promise<void> => {
		event.stopPropagation();
		if (this.copying) return;
		this.copying = true;
		try {
			await navigator.clipboard.writeText(this.card.code);
			this.copyResult = "copied";
		} catch {
			this.copyResult = "failed";
		} finally {
			this.copying = false;
		}
	};

	/** The card activates on click, mousedown and Enter/Space; its three other
	 *  listeners (mouseup, mouseleave, keyup) only clear the press wash, so
	 *  guarding these two beside copy's own click is the complete set. Narrow to
	 *  those two keys and no wider: this same button carries a tooltip that
	 *  dismisses on Escape through a document listener registered without
	 *  capture, so stopping every keydown would trap the tooltip open on the one
	 *  focus state that reveals it. */
	private readonly keepPressOffTheCard = (event: Event): void => {
		if (
			event instanceof KeyboardEvent &&
			event.key !== "Enter" &&
			event.key !== " "
		)
			return;
		event.stopPropagation();
	};

	/** The same sheet the list's own New button opens, mounted from the card
	 *  that owns the code — <voucher-form> reads its world off the shared
	 *  singletons and takes the event and the code as its whole surface. */
	private readonly open = (): void => {
		const eventId = consoleEventId.value;
		if (!eventId) return;
		show(
			() =>
				html`<voucher-form
					.eventId=${eventId}
					.code=${this.card.code}
				></voucher-form>`,
			{ ariaLabel: this.card.code },
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	};
}

declare global {
	interface HTMLElementTagNameMap {
		"voucher-card": VoucherCard;
	}
}
